import React from 'react';

import { Button, TitleWithDescription, InfoBox } from '@infoods/lifebase-ui';
import { enqueueSnackbar } from 'notistack';
import { useToggle } from 'react-use';

import classes from './RegistrationFormAccessWidget.module.scss';
import { ReactComponent as QrCodeIcon } from '../../assets/icons/qr-code.svg';
import { ReactComponent as WebsiteLinkIcon } from '../../assets/icons/website-link.svg';
import { copyTextToClipboard } from '../../../shared/utils/apiUtils';
import { RegistrationQrCodeModal } from '../RegistrationQrCodeModal/RegistrationQrCodeModal';

type RegistrationFormAccessWidgetProps = {
  id: number;
  getRegistrationLink: (id: number) => Promise<{ url: string }>;
  getRegistrationQrCode: (id: number) => Promise<{ fileUrl: string }>;
  qrCodeDescription: string;
};

const RegistrationFormAccessWidget = ({
  id,
  getRegistrationLink,
  getRegistrationQrCode,
  qrCodeDescription,
}: RegistrationFormAccessWidgetProps) => {
  const [isQrCodeModalOpen, toggleQrCodeModalOpen] = useToggle(false);

  const handleCopyLink = async () => {
    const { url } = await getRegistrationLink(id);

    copyTextToClipboard(url);
    enqueueSnackbar('The link to the user registration form was successfully copied', {
      variant: 'success',
      autoHideDuration: 2000,
    });
  };

  return (
    <>
      <InfoBox title="Access to Registration Form">
        <div className={classes.row}>
          <div className={classes.box}>
            <div className={classes.info}>
              <TitleWithDescription
                title="QR code access"
                description={qrCodeDescription}
                titleFont="body-14-bold"
              />
              <Button variant="outlined" size="small" onClick={toggleQrCodeModalOpen}>
                Preview and Download
              </Button>
            </div>
            <div className={classes.icon}>
              <QrCodeIcon />
            </div>
          </div>
          <div className={classes.box}>
            <div className={classes.info}>
              <TitleWithDescription
                title="Access to the registration form via the link"
                description="Copy the permanent link to share with your customers"
                titleFont="body-14-bold"
              />
              <Button variant="outlined" size="small" onClick={handleCopyLink}>
                Copy link
              </Button>
            </div>
            <div className={classes.icon}>
              <WebsiteLinkIcon />
            </div>
          </div>
        </div>
      </InfoBox>
      {isQrCodeModalOpen && (
        <RegistrationQrCodeModal
          id={id}
          getRegistrationQrCode={getRegistrationQrCode}
          onClose={toggleQrCodeModalOpen}
        />
      )}
    </>
  );
};

export { RegistrationFormAccessWidget };
